import { zodResolver } from "@hookform/resolvers/zod";
import { observer } from "mobx-react-lite";
import { type FC, useEffect } from "react";
import { useForm } from "react-hook-form";

import { OptionType, useGetOptionsQuery } from "@/api/admin";
import { CustomCardBody, LabelWithOptional, Loader } from "@/components/atoms";
import {
  InputControl,
  SelectControl,
  toastWarning,
} from "@/components/molecules";
import { Form } from "@/components/ui/form";
import { cn } from "@/lib/utils";
import type { FormProps as BaseFormProps } from "@/types/common";
import { mapToTreeData } from "@/utils/mapToTreeData";

import { getFormSchema, type GroupInformationFormType } from "./types";

interface FormProps<T> extends BaseFormProps<T> {
  isWide?: boolean;
  isEditable?: boolean;
}

const GroupInformationForm: FC<FormProps<GroupInformationFormType>> = observer(
  ({
    defaultValues,
    onSubmit,
    onValidityChange,
    id,
    setValidData,
    children,
    classes,
    isWide = false,
    isEditable = false,
  }) => {
    const form = useForm<GroupInformationFormType>({
      resolver: zodResolver(getFormSchema()),
      defaultValues,
      mode: "onChange",
      reValidateMode: "onBlur",
    });

    const { data: optionsData, isPending: isOptionPending } =
      useGetOptionsQuery([
        OptionType.Currencies,
        OptionType.Countries,
        OptionType.LegalEntityWithHierarchy,
      ]);

    const {
      control,
      handleSubmit,
      watch,
      formState: { isValid },
    } = form;

    useEffect(() => {
      onValidityChange?.(isValid);
    }, [isValid, onValidityChange]);

    useEffect(() => {
      const subscription = watch(values => {
        setValidData?.(values as GroupInformationFormType);
      });
      return () => subscription.unsubscribe();
    }, [watch, onValidityChange, setValidData]);

    const treeData = mapToTreeData(
      optionsData?.legalEntitiesWithHierarchy ?? []
    );

    const initialLegalEntityId = defaultValues?.legalEntityId;

    useEffect(() => {
      if (!initialLegalEntityId) return;

      const subscription = watch((values, { name }) => {
        if (
          name === "legalEntityId" &&
          values.legalEntityId &&
          values.legalEntityId !== initialLegalEntityId
        ) {
          toastWarning(
            "Changing Legal Entity may affect related data. Please review carefully."
          );
        }
      });

      return () => subscription.unsubscribe();
    }, [watch, initialLegalEntityId]);

    if (isOptionPending) {
      return <Loader fullscreen />;
    }

    return (
      <Form {...form}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          id={id}
          data-testid="group-information-form"
        >
          <CustomCardBody classes={cn("pb-8", classes)}>
            <div>
              <div
                className={cn(
                  !isWide && "flex align-center gap-x-4 flex-col sm:flex-row"
                )}
              >
                <InputControl<GroupInformationFormType>
                  formControl={control}
                  label="Reference number"
                  nameField="referenceNumber"
                  disabled
                  data-testid="group-information-form-reference-number"
                />

                <InputControl<GroupInformationFormType>
                  formControl={control}
                  label="Group name"
                  nameField="name"
                  data-testid="group-information-form-group-name"
                />
              </div>

              <div className="flex align-center gap-x-0 sm:gap-x-4 gap-y-4 sm:gap-y-0 mb-4.5 flex-col sm:flex-row">
                <SelectControl<GroupInformationFormType>
                  form={form}
                  nameSelect="country"
                  label="Country"
                  placeholder="Select"
                  disabled={isEditable}
                  itemsList={optionsData?.countries || []}
                  triggerProps={{
                    "data-testid": "group-information-form-country",
                  }}
                />
                <SelectControl<GroupInformationFormType>
                  form={form}
                  nameSelect="currency"
                  label="Currency"
                  placeholder="Select"
                  disabled={isEditable}
                  itemsList={optionsData?.currencies || []}
                  triggerProps={{
                    "data-testid": "group-information-form-currency",
                  }}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 text-sm mb-4.5">
                <div className="flex flex-col gap-1">
                  <LabelWithOptional label="Legal entity" />
                  <SelectControl<GroupInformationFormType>
                    form={form}
                    nameSelect="legalEntityId"
                    placeholder="Select"
                    isTree
                    itemsList={treeData}
                    triggerProps={{
                      "data-testid": "group-information-form-legal-entity",
                    }}
                  />
                </div>
              </div>

              {children}
            </div>
          </CustomCardBody>
        </form>
      </Form>
    );
  }
);

export default GroupInformationForm;
